import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { read, command, clientPath, type Switch } from '@/services/vault/vault.http'
import { actionLabel, Field, useCommand, useStepUp, CommandStatus, RETENTION } from './VaultForms'

export default function FirmSwitchesPage() {
  const { user } = useAuth(),
    [status, setStatus] = useState('pending_acceptance')
  const rows = useQuery({
    queryKey: ['vault', user?.id, user?.authScope, 'incoming', status],
    queryFn: () => read<{ items: Switch[] }>('/firm-client-switches', { status }),
    staleTime: 0,
  })
  return (
    <div className="space-y-4 text-text-primary">
      <h1 className="text-2xl font-semibold">Incoming client switch requests</h1>
      <p className="text-text-secondary">
        Review the client’s explicit service scopes before accepting. Acceptance closes the former
        relationship and grants your firm only the scopes shown.
      </p>
      <p className="text-sm text-text-secondary">{RETENTION}</p>
      <label className="block text-sm text-text-secondary">
        Status
        <select
          className="input input-bordered w-full"
          value={status}
          onChange={event => setStatus(event.target.value)}
        >
          {['pending_acceptance', 'completed', 'cancelled', 'rejected', 'expired'].map(value => (
            <option key={value}>{value}</option>
          ))}
        </select>
      </label>
      {rows.error && <p role="alert">{rows.error.message}</p>}
      {rows.data?.items.length === 0 && <p>No requests with this status.</p>}
      {rows.data?.items.map(row => (
        <Decision
          key={row.switch_id}
          row={row}
          refresh={async () => {
            await rows.refetch()
          }}
        />
      ))}
    </div>
  )
}
function Decision({ row, refresh }: { row: Switch; refresh: () => Promise<void> }) {
  const actions = useCommand(),
    stepUp = useStepUp(),
    [code, setCode] = useState(''),
    [ack, setAck] = useState(false)
  return (
    <article className="card card-elevated p-5 space-y-3">
      <h2 className="font-semibold break-all">Client {row.client_id}</h2>
      <p className="text-sm text-text-secondary">
        Former firm {row.source_firm_id} · deadline {row.expires_at} · {row.status} · revision{' '}
        {row.revision}
      </p>
      {row.targets.map(target => (
        <div className="border border-surface-overlay rounded-lg p-3 text-sm" key={target.service}>
          <p>
            {target.service}: {target.scope.actions.map(actionLabel).join(', ')}
          </p>
          <p>Categories: {target.scope.categories.join(', ')}</p>
          <p>
            Years: {target.scope.financial_years.join(', ')} · Grant expiry:{' '}
            {target.grant_expires_at ?? 'None specified'}
          </p>
        </div>
      ))}
      <p className="text-sm text-text-secondary">Client consent terms: {row.terms_version}</p>
      {row.status === 'completed' && (
        <Link
          to={`/firm-vault/${row.client_id}`}
          className="text-primary-600 dark:text-primary-400"
        >
          Open authorized vault
        </Link>
      )}
      {row.status === 'pending_acceptance' && (
        <>
          <label className="flex gap-2 text-sm text-text-secondary">
            <input type="checkbox" checked={ack} onChange={event => setAck(event.target.checked)} />
            I reviewed the exact scopes and retained read-only history.
          </label>
          <Field
            label="Fresh authenticator or backup code"
            value={code}
            autoComplete="one-time-code"
            onChange={event => setCode(event.target.value)}
          />
          <div className="flex gap-2">
            {(['accept', 'reject'] as const).map(decision => (
              <button
                className={decision === 'accept' ? 'btn btn-primary' : 'btn btn-secondary'}
                key={decision}
                disabled={actions.busy || !ack || !code.trim()}
                onClick={() =>
                  void actions.run(async () => {
                    await stepUp(code)
                    await command(
                      `${clientPath(row.client_id)}/switches/${row.switch_id}:${decision}`,
                      {
                        expected_revision: row.revision,
                        idempotency_key: actions.idempotencyKey({
                          decision,
                          switch: row.switch_id,
                          revision: row.revision,
                        }),
                      }
                    )
                    setCode('')
                    setAck(false)
                    await refresh()
                  })
                }
              >
                {decision === 'accept' ? 'Accept exact scopes' : 'Reject request'}
              </button>
            ))}
          </div>
        </>
      )}
      <CommandStatus {...actions} />
    </article>
  )
}
